import { useEffect, useRef, useCallback, useState } from 'react';
import { useBaaSAuth } from '@/contexts/BaaSAuthContext';

const TIMEOUT_MS = 5 * 60 * 1000;
const WARNING_MS = 30 * 1000;
const THROTTLE_MS = 30 * 1000;
const BROWSER_CLOSE_KEY = 'baas_session_active';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'keyup', 'touchstart', 'touchmove', 'scroll', 'wheel'];

export function useBaaSSessionTimeout() {
  const { user, signOut } = useBaaSAuth();
  const timeoutMsRef = useRef(TIMEOUT_MS);
  const lastActivityRef = useRef(Date.now());
  const lastResetRef = useRef(0);
  const prevUserRef = useRef<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  const showCountdownRef = useRef(false);

  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const doSignOut = useCallback(() => {
    clearTimers();
    setShowCountdown(false);
    showCountdownRef.current = false;
    setCountdownSeconds(0);
    sessionStorage.removeItem(BROWSER_CLOSE_KEY);
    signOutRef.current();
  }, [clearTimers]);

  const scheduleTimers = useCallback((ms: number) => {
    clearTimers();
    setShowCountdown(false);
    showCountdownRef.current = false;
    setCountdownSeconds(0);

    warningRef.current = setTimeout(() => {
      setShowCountdown(true);
      showCountdownRef.current = true;
      setCountdownSeconds(30);
      countdownRef.current = setInterval(() => {
        setCountdownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }, ms - WARNING_MS);

    timerRef.current = setTimeout(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const currentMs = timeoutMsRef.current;
      if (elapsed < currentMs - 1000) {
        scheduleTimers(currentMs - elapsed);
        return;
      }
      doSignOut();
    }, ms);
  }, [clearTimers, doSignOut]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastResetRef.current = Date.now();
    scheduleTimers(timeoutMsRef.current);
  }, [scheduleTimers]);

  useEffect(() => {
    if (!user) {
      prevUserRef.current = null;
      sessionStorage.removeItem(BROWSER_CLOSE_KEY);
      clearTimers();
      return;
    }

    const isFreshLogin = prevUserRef.current === null;
    prevUserRef.current = user.id;

    if (isFreshLogin) {
      sessionStorage.setItem(BROWSER_CLOSE_KEY, '1');
    } else {
      const wasActive = sessionStorage.getItem(BROWSER_CLOSE_KEY);
      if (!wasActive) {
        signOutRef.current();
        return;
      }
      sessionStorage.setItem(BROWSER_CLOSE_KEY, '1');
    }

    lastActivityRef.current = Date.now();
    lastResetRef.current = Date.now();
    scheduleTimers(timeoutMsRef.current);

    const handler = () => {
      lastActivityRef.current = Date.now();
      if (showCountdownRef.current) {
        lastResetRef.current = Date.now();
        scheduleTimers(timeoutMsRef.current);
        return;
      }
      if (Date.now() - lastResetRef.current > THROTTLE_MS) {
        lastResetRef.current = Date.now();
        scheduleTimers(timeoutMsRef.current);
      }
    };

    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, handler, { passive: true }));

    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        const currentMs = timeoutMsRef.current;
        if (elapsed >= currentMs) {
          clearTimers();
          setShowCountdown(false);
          showCountdownRef.current = false;
          setCountdownSeconds(0);
          sessionStorage.removeItem(BROWSER_CLOSE_KEY);
          signOutRef.current();
        } else if (elapsed >= currentMs - WARNING_MS) {
          const remaining = currentMs - elapsed;
          clearTimers();
          setShowCountdown(true);
          showCountdownRef.current = true;
          setCountdownSeconds(Math.ceil(remaining / 1000));
          countdownRef.current = setInterval(() => {
            setCountdownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
          }, 1000);
          timerRef.current = setTimeout(() => {
            clearTimers();
            setShowCountdown(false);
            showCountdownRef.current = false;
            sessionStorage.removeItem(BROWSER_CLOSE_KEY);
            signOutRef.current();
          }, remaining);
        } else {
          lastResetRef.current = Date.now();
          scheduleTimers(currentMs - elapsed);
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, handler));
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { showCountdown, countdownSeconds, resetTimer };
}
